import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { applyDocLocale } from '../lib/doc';
import { setFormatLocale } from '../lib/format';
import { cs, type I18nKey } from './cs';

export type { I18nKey } from './cs';

import { en } from './en';

/**
 * i18n webu (fáze 14): dva slovníky se stejnými klíči, `t(key, vars)` s interpolací `{n}`
 * a plurály `jedna|2–4|ostatní` (česky) / `one|other|other` (anglicky). Jazyk: uložený na
 * účtu (`User.locale`) → localStorage → jazyk prohlížeče. `setLocale` uloží obojí.
 */
export type Locale = 'cs' | 'en';
export const LOCALES: Locale[] = ['cs', 'en'];
export const LOCALE_STORAGE_KEY = 'isupply.locale';
const DICTS: Record<Locale, Record<I18nKey, string>> = { cs, en };

export function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return v === 'cs' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

export function detectLocale(): Locale {
  const stored = readStoredLocale();
  if (stored) return stored;
  const nav = typeof navigator !== 'undefined' ? navigator.language : 'cs';
  return nav.toLowerCase().startsWith('cs') || nav.toLowerCase().startsWith('sk') ? 'cs' : 'en';
}

/** Česká pravidla: 1 → první tvar, 2–4 → druhý, jinak třetí; anglicky 1 → první, jinak druhý. */
export function pluralIndex(locale: Locale, n: number): number {
  const a = Math.abs(n);
  if (locale === 'cs') return a === 1 ? 0 : a >= 2 && a <= 4 ? 1 : 2;
  return a === 1 ? 0 : 1;
}

export function translate(
  locale: Locale,
  key: I18nKey,
  vars: Record<string, string | number> = {},
): string {
  let text = DICTS[locale][key] ?? DICTS.cs[key] ?? key;
  if (text.includes('|') && typeof vars.n === 'number') {
    const forms = text.split('|');
    text = forms[Math.min(pluralIndex(locale, vars.n), forms.length - 1)] ?? text;
  }
  return text.replace(/\{(\w+)\}/g, (_m, k: string) =>
    vars[k] === undefined ? `{${k}}` : String(vars[k]),
  );
}

interface I18nCtx {
  locale: Locale;
  t: (key: I18nKey, vars?: Record<string, string | number>) => string;
  setLocale: (locale: Locale) => void;
}

const Ctx = createContext<I18nCtx | null>(null);

export function I18nProvider({ children, initial }: { children: ReactNode; initial?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(initial ?? detectLocale());
  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, l);
    } catch {
      /* bez úložiště */
    }
  }, []);
  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);
  applyDocLocale(locale);
  setFormatLocale(locale);
  const value = useMemo<I18nCtx>(
    () => ({ locale, t: (key, vars) => translate(locale, key, vars), setLocale }),
    [locale, setLocale],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n mimo I18nProvider');
  return ctx;
}

export function useT() {
  return useI18n().t;
}

/** Aktuální jazyk mimo React (chybové hlášky v api/client.ts). */
export function currentLocale(): Locale {
  return readStoredLocale() ?? 'cs';
}
